import { AppShell, PageTitle } from "@/components/app-shell";
import { Skeleton } from "@/components/ui/skeleton";
import { nextUp } from "@/lib/mission-guide";
import { useProjectScope } from "@/lib/project-scope";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  artifactMeta,
  ApiError,
  type Artifact,
  type HardwareRevision,
  filesForRevision,
  filesViewerPath,
  filterTree,
  HARDWARE_RELOAD_EVENT,
  mergeRevisionFileTree,
  selectionForRevisionTree,
  getArtifactTree,
  getHardware,
  getProject,
  getRevisionFileCatalog,
  hwCollabEnabled,
  revisionHasPcb,
  shortOid,
} from "@/lib/api";
import { isKicadViewerPath, normalizeTreePath, pathInRevisionTree } from "@/lib/kicanvas-sources";
import { viewerAutoloadAllowed } from "@/lib/kicanvas-viewer-load";
import {
  applyHardwareRevisionPrefetch,
  filesUrlRevisionId,
  prefetchKnownRevisionFromRoute,
  resolveHostedRevisionDisplay,
} from "@/lib/revision-file-prefetch";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { HardwareSkeleton } from "@/components/mission-skeletons";
import { KicadFileView } from "@/components/kicad-file-view";
import { ProjectMaterialsPanel } from "@/components/project-materials";
import { MISSION_SELECT_FILE_EVENT, MISSION_SET_FILTER_EVENT } from "@/lib/mission-drive";
import { filePathFromSearch, nextFilesLocation, OPEN_MISSION_PANEL_EVENT } from "@/lib/mission-navigator";
import { requestOpenThinkPicker } from "@/lib/underline-nav";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";

type Chip = { id: string; label: string; git_oid: string; has_pcb: boolean };

function FilesNextUp() {
  const scope = useProjectScope();
  const base =
    scope.projectBase ||
    (scope.orgSlug && scope.projectSlug ? `/o/${scope.orgSlug}/p/${scope.projectSlug}` : "");
  if (scope.loading) {
    return (
      <div className="mb-4 flex items-center gap-2" data-testid="files-nextup-skeleton" role="status">
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-7 w-28 rounded-full" />
      </div>
    );
  }
  const up = nextUp(scope.home, base);
  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2"
      data-testid="files-nextup"
    >
      <span className="text-[11px] font-semibold tracking-[0.08em] text-[var(--copper)] uppercase">Next up</span>
      <span className="text-[13px] text-foreground">{up.title}</span>
      <Link
        to={up.primary.to}
        className="inline-flex items-center rounded-full border border-border px-3 py-1 text-[12px] text-foreground no-underline hover:bg-white/[0.08]"
      >
        {up.primary.label}
      </Link>
      <Link
        to={up.secondary.to}
        className="inline-flex items-center rounded-full px-3 py-1 text-[12px] text-muted-foreground no-underline hover:text-foreground"
      >
        {up.secondary.label}
      </Link>
    </div>
  );
}

export function HardwarePage() {
  const { orgSlug, projectSlug, revId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const scope = useProjectScope();
  const urlFile = filePathFromSearch(location.search);
  const urlRev = filesUrlRevisionId({
    routeRevId: revId,
    pathname: location.pathname,
    search: location.search,
  });
  const [live, setLive] = useState(false);
  const [revisions, setRevisions] = useState<HardwareRevision[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const { activeRevId: active, unknownUrlRev: unknownUrlRevRaw } = useMemo(
    () => resolveHostedRevisionDisplay(urlRev, revisions),
    [urlRev, revisions],
  );
  const [hardwareListingComplete, setHardwareListingComplete] = useState(false);
  const unknownUrlRev = hardwareListingComplete ? unknownUrlRevRaw : null;
  const [tree, setTree] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [filesSurface, setFilesSurface] = useState<"tree" | "materials">(
    new URLSearchParams(location.search).get("filter") === "materials" ? "materials" : "tree",
  );
  const [selectedPath, setSelectedPath] = useState<string | null>(() => urlFile);
  const selectedPathRef = useRef(selectedPath);
  selectedPathRef.current = selectedPath;
  const locationRef = useRef(location);
  locationRef.current = location;
  const revisionsRef = useRef(revisions);
  revisionsRef.current = revisions;
  const revIdRef = useRef(revId);
  revIdRef.current = revId;
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  const [treeReady, setTreeReady] = useState(false);
  const [treeRevId, setTreeRevId] = useState<string | null>(null);
  const [listingComplete, setListingComplete] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const treeFetchGen = useRef(0);
  const urlUnknownFixKey = useRef<string | null>(null);
  const [unknownRevNotice, setUnknownRevNotice] = useState<string | null>(null);
  const [catalogRevId, setCatalogRevId] = useState<string | null>(null);
  const [catalogReady, setCatalogReady] = useState(false);
  const [catalogByteSizes, setCatalogByteSizes] = useState<Map<string, number | null>>(() => new Map());
  useEffect(() => {
    if (unknownUrlRev) setUnknownRevNotice(unknownUrlRev);
  }, [unknownUrlRev]);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);

  const persistFilesUrl = useCallback(
    (update: { rev?: string; file?: string }) => {
      const loc = locationRef.current;
      let payload = update;
      if (update.file !== undefined && update.rev === undefined) {
        const revInUrl = filesUrlRevisionId({
          routeRevId: revIdRef.current,
          pathname: loc.pathname,
          search: loc.search,
        });
        const rev =
          revInUrl ??
          resolveHostedRevisionDisplay(revInUrl, revisionsRef.current).activeRevId ??
          undefined;
        payload = { ...update, rev };
      }
      const next = nextFilesLocation(loc, payload);
      if (!next) return;
      navigate({ pathname: next.pathname, search: next.search, hash: next.hash }, { replace: true });
    },
    [navigate],
  );

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      setHardwareListingComplete(false);
      // Apply chips/active as soon as GET …/hardware lands. Do not wait for
      // getProject (settings / hw_collab) — that was the delayToFile URL stall.
      const hwTask = getHardware(orgSlug, projectSlug).then((hw) => {
        if (!isCurrent()) return hw;
        const revs = hw.revisions ?? [];
        const filePath = selectedPathRef.current ?? filePathFromSearch(searchRef.current);
        const knownFromRoute = filesUrlRevisionId({
          routeRevId: revId,
          pathname: locationRef.current.pathname,
          search: searchRef.current,
        });
        // F3a already started the GET for a URL-known (rev, file). Listing-picked
        // rev still prefetches here (DELAY F1). Pass filePath: null to skip the
        // duplicate entry — do not start a second prefetch for the same file.
        applyHardwareRevisionPrefetch({
          orgSlug,
          projectSlug,
          knownRevId: knownFromRoute,
          filePath: null,
          revisions: revs,
        });
        setRevisions(revs);
        setArtifacts(hw.artifacts ?? []);
        setHardwareListingComplete(true);
        return hw;
      });
      const [proj] = await Promise.all([getProject(orgSlug, projectSlug), hwTask]);
      if (!isCurrent()) return;
      // Seed demo oids stay hidden until the owner flag is on; D1 chips are per-project.
      setLive(hwCollabEnabled(proj.project.settings_json));
      setAccessDenied(false);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setLive(false);
      const denied = e instanceof ApiError && e.status === 403;
      setAccessDenied(denied);
      setErr(denied ? null : e instanceof Error ? e.message : "hardware load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, revId, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (scope.hardware.length === 0) return;
    // Home is LIMIT 5 — only seed chips. Never prefetch the revision file here
    // (F3a / F1 already owns that GET) and never validate a known/API rev against it.
    setLive(true);
    setRevisions((prev) => (prev.length > 0 ? prev : scope.hardware));
  }, [scope.hardware]);

  useEffect(() => {
    const onReload = () => {
      void load();
    };
    window.addEventListener(HARDWARE_RELOAD_EVENT, onReload);
    return () => window.removeEventListener(HARDWARE_RELOAD_EVENT, onReload);
  }, [load]);

  useEffect(() => {
    if (location.hash !== "#upload") return;
    requestOpenThinkPicker();
  }, [location.hash, location.key]);

  useEffect(() => {
    const onPanel = (event: Event) => {
      const panel = (event as CustomEvent<{ panel?: string }>).detail?.panel;
      if (panel === "materials") setFilesSurface("materials");
      if (panel === "tree") setFilesSurface("tree");
    };
    const onFile = (event: Event) => {
      const path = (event as CustomEvent<{ path?: string }>).detail?.path;
      if (typeof path === "string" && path.trim()) {
        const next = path.trim();
        setFilesSurface("tree");
        const loc = locationRef.current;
        const rev = resolveHostedRevisionDisplay(
          filesUrlRevisionId({
            routeRevId: revId,
            pathname: loc.pathname,
            search: loc.search,
          }),
          revisionsRef.current,
        ).activeRevId;
        persistFilesUrl({ file: next });
      }
    };
    const onFilter = (event: Event) => {
      const detail = (event as CustomEvent<{ page?: string; filter?: string }>).detail;
      if (detail?.page !== "files") return;
      if (detail.filter === "materials") setFilesSurface("materials");
      if (detail.filter === "tree") setFilesSurface("tree");
    };
    window.addEventListener(OPEN_MISSION_PANEL_EVENT, onPanel);
    window.addEventListener(MISSION_SELECT_FILE_EVENT, onFile);
    window.addEventListener(MISSION_SET_FILTER_EVENT, onFilter);
    return () => {
      window.removeEventListener(OPEN_MISSION_PANEL_EVENT, onPanel);
      window.removeEventListener(MISSION_SELECT_FILE_EVENT, onFile);
      window.removeEventListener(MISSION_SET_FILTER_EVENT, onFilter);
    };
  }, [persistFilesUrl, revId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get("filter");
    const file = filePathFromSearch(location.search);
    if (filter === "materials") setFilesSurface("materials");
    if (filter === "tree") setFilesSurface("tree");
    if (file) {
      setFilesSurface("tree");
      setSelectedPath((prev) => (prev === file ? prev : file));
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!hardwareListingComplete || !unknownUrlRev || !active) return;
    const fixKey = `${unknownUrlRev}->${active}`;
    if (urlUnknownFixKey.current === fixKey) return;
    urlUnknownFixKey.current = fixKey;
    persistFilesUrl({ rev: active });
  }, [hardwareListingComplete, unknownUrlRev, active, persistFilesUrl]);

  const chips: Chip[] = useMemo(() => {
    if (!live) return [];
    return revisions.map((r) => ({
      id: r.id,
      label: r.label || shortOid(r.git_oid),
      git_oid: r.git_oid,
      has_pcb: revisionHasPcb(r),
    }));
  }, [live, revisions]);

  const liveRev = live ? revisions.find((r) => r.id === active) : undefined;

  useEffect(() => {
    if (!live || !liveRev || !orgSlug || !projectSlug) {
      setCatalogByteSizes(new Map());
      setCatalogRevId(null);
      setCatalogReady(!live);
      return;
    }
    const rev = liveRev.id;
    let cancelled = false;
    setCatalogReady(false);
    void getRevisionFileCatalog(orgSlug, projectSlug, rev)
      .then((body) => {
        if (cancelled) return;
        const map = new Map<string, number | null>();
        for (const row of body.files ?? []) {
          map.set(normalizeTreePath(row.path), row.byte_size);
        }
        setCatalogByteSizes(map);
        setCatalogRevId(rev);
        setCatalogReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCatalogByteSizes(new Map());
        setCatalogRevId(rev);
        setCatalogReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [live, liveRev, orgSlug, projectSlug]);

  useEffect(() => {
    if (!catalogReady || !orgSlug || !projectSlug || !active) return;
    if (catalogRevId !== active) return;
    const file = selectedPathRef.current ?? filePathFromSearch(searchRef.current);
    if (!file || !isKicadViewerPath(file)) return;
    const size = catalogByteSizes.get(normalizeTreePath(file)) ?? null;
    if (!viewerAutoloadAllowed(size)) return;
    prefetchKnownRevisionFromRoute({
      orgSlug,
      projectSlug,
      routeRevId: revId,
      pathname: location.pathname,
      search: location.search,
      filePath: file,
      catalogByteSize: size,
    });
  }, [catalogReady, catalogRevId, catalogByteSizes, active, orgSlug, projectSlug, revId, location.pathname, location.search]);

  useEffect(() => {
    if (!live) {
      if (loading) return;
      setTree([]);
      setTreeRevId(null);
      setTreeReady(true);
      setListingComplete(true);
      return;
    }
    const gen = ++treeFetchGen.current;
    if (!liveRev || !orgSlug || !projectSlug) {
      setTree([]);
      setTreeRevId(null);
      setTreeReady(false);
      setListingComplete(false);
      return;
    }
    const metaFiles = filterTree(filesForRevision(artifacts, liveRev), revisionHasPcb(liveRev));
    const rev = liveRev.id;
    const keepViewer = treeRevId === rev;
    // QA r19: do not hold treeReady for GET …/tree. That used to arrayBuffer()
    // the whole zip and blocked the first revision-file GET by ~30s.
    setTree((prev) => (keepViewer && prev.length > 0 ? prev : metaFiles));
    setTreeRevId(rev);
    setTreeReady(true);
    if (!keepViewer) setListingComplete(false);
    const zip = artifacts.find((a) => a.kind === "kicad_zip" && a.git_oid === liveRev.git_oid);
    if (!zip) {
      setListingComplete(true);
      return;
    }
    void (async () => {
      try {
        const body = await getArtifactTree(orgSlug, projectSlug, zip.id);
        if (treeFetchGen.current !== gen) return;
        setTree(
          filterTree(mergeRevisionFileTree(metaFiles, body.paths ?? []), revisionHasPcb(liveRev)),
        );
        setTreeRevId(rev);
        setListingComplete(true);
      } catch {
        if (treeFetchGen.current !== gen) return;
        setTree(metaFiles);
        setTreeRevId(rev);
        setListingComplete(true);
      }
    })();
    return () => {
      treeFetchGen.current += 1;
    };
  }, [loading, live, liveRev, artifacts, orgSlug, projectSlug]);

  useEffect(() => {
    if (!treeReady || treeRevId !== active) return;
    setSelectedPath((prev) => {
      if (!prev) return prev;
      const next = selectionForRevisionTree(prev, tree);
      // Keep ?file= when it is not in the tree so filesViewerPath can mark missing.
      return next ?? prev;
    });
  }, [tree, active, treeReady, treeRevId]);

  const viewerDecision = filesViewerPath({
    selectedPath,
    tree,
    treeReady,
    treeRevId,
    activeRevId: active,
    listingComplete,
    pathInTree: pathInRevisionTree,
  });
  const viewerMissing = viewerDecision.missing;
  const missingMarkerPath = viewerDecision.path ?? selectedPath;
  // Opening KiCanvas is a viewer-type gate; missing is decided above.
  const viewerPath = viewerMissing ? null : viewerDecision.path;
  const viewerTree = useMemo(() => {
    if (viewerPath && !pathInRevisionTree(viewerPath, tree)) {
      return mergeRevisionFileTree(tree, [viewerPath]);
    }
    return tree;
  }, [tree, viewerPath]);
  return (
    <AppShell
      title={
        <PageTitle
          kicker="KiCad SoT · hosted revisions"
          extra={
            <Button
              size="sm"
              variant="outline"
              data-testid="files-open-think-upload"
              data-mission-id="action:upload-zip"
              data-mission-panel="upload"
              data-mission-danger=""
              onClick={() => requestOpenThinkPicker()}
            >
              Upload .zip
            </Button>
          }
        >
          Files
        </PageTitle>
      }
    >
      <FilesNextUp />
      <Alert className="mb-4">
        <AlertTitle>KiCad project hosting</AlertTitle>
        <AlertDescription>
          Drop a KiCad zip on Think to stage it. Think decides when to import — Queue → runner is the only write path.
          Open a .kicad_sch, .kicad_pcb, or .kicad_pro in the tree to view it in KiCanvas.
        </AlertDescription>
      </Alert>
      {unknownRevNotice && active ? (
        <Alert className="mb-4" data-testid="files-revision-unknown">
          <AlertTitle>Revision not found</AlertTitle>
          <AlertDescription>
            Hosted revision <span className="font-mono">{unknownRevNotice}</span> is not in this project.
            Showing <span className="font-mono">{active}</span> instead; the URL was updated to match.
          </AlertDescription>
        </Alert>
      ) : null}
      {accessDenied ? (
        <div
          className="flex min-h-[420px] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-6 py-12 text-center"
          data-testid="files-no-access"
          role="alert"
        >
          <p className="mb-1 text-sm font-medium">No access to this project</p>
          <p className="max-w-md text-sm text-muted-foreground">
            You are not a member of this project. Home, Files, and project APIs returned 403.
          </p>
        </div>
      ) : null}
      {err && !accessDenied && <p className="mb-3 text-sm text-destructive">{err}</p>}
      <div className="mb-3 flex flex-wrap items-center gap-1" data-testid="files-sidebar-toggle">
        <Button
          size="sm"
          variant={filesSurface === "tree" ? "default" : "outline"}
          data-testid="files-sidebar-tree"
          data-mission-id="panel:tree"
          data-mission-panel="tree"
          onClick={() => setFilesSurface("tree")}
        >
          TREE
        </Button>
        <Button
          size="sm"
          variant={filesSurface === "materials" ? "default" : "outline"}
          data-testid="files-sidebar-materials"
          data-mission-id="panel:materials"
          data-mission-panel="materials"
          onClick={() => setFilesSurface("materials")}
        >
          Materials
        </Button>
      </div>
      {loading && !active ? (
        <HardwareSkeleton />
      ) : accessDenied ? null : filesSurface === "materials" ? (
        <div
          className="rounded-lg border border-border bg-card p-4"
          data-testid="files-materials"
        >
          <p className="mb-3 text-[13px] text-muted-foreground">
            Project knowledge library (upload and index status). Semantic search in the dock is
            temporarily off — status is the row from Workbench, never a fake index.
          </p>
          <ProjectMaterialsPanel />
        </div>
      ) : live && chips.length === 0 ? (
        <div
          className="flex min-h-[420px] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-6 py-12 text-center"
          data-testid="hardware-empty-upload"
        >
          <p className="mb-1 text-sm font-medium">No hosted revisions yet</p>
          <p className="mb-4 max-w-md text-sm text-muted-foreground">
            Source of truth is a KiCad project zip on this platform (R2 + libgit2). GitHub is optional sync, not the
            host. Stage the zip in Think — import is a background job.
          </p>
          <Button
            size="sm"
            data-testid="files-open-think-upload"
            data-mission-id="action:upload-zip"
            data-mission-panel="upload"
            data-mission-danger=""
            onClick={() => requestOpenThinkPicker()}
          >
            Upload KiCad zip
          </Button>
        </div>
      ) : (
        <>
      <div className="mb-3 flex flex-wrap gap-2">
        {chips.length === 0 && (
          <span className="text-sm text-muted-foreground">No hosted revisions yet.</span>
        )}
        {chips.map((c) => (
          <Button
            key={c.id}
            size="sm"
            variant={c.id === active ? "default" : "outline"}
            data-testid={`revision-chip-${c.id}`}
            data-revision-active={c.id === active ? "true" : "false"}
            onClick={() => {
              setUnknownRevNotice(null);
              persistFilesUrl({ rev: c.id });
            }}
          >
            <span>{c.label}</span>
            <span className="font-mono text-[10px] opacity-80">{shortOid(c.git_oid)}</span>
          </Button>
        ))}
      </div>
      <div className="grid min-h-[420px] min-w-0 grid-cols-1 overflow-auto rounded-lg border border-border md:grid-cols-[minmax(0,280px)_minmax(240px,1fr)]">
        <div className="max-h-[520px] min-w-0 overflow-auto border-r border-border bg-card p-3 font-mono text-xs">
          <div className="mb-2 text-[11px] tracking-wide text-muted-foreground uppercase">tree</div>
          {tree.slice(0, 400).map((path) => (
            <button
              type="button"
              key={path}
              data-mission-id={`file:${path}`}
              data-testid={`file-row-${path}`}
              onClick={() => persistFilesUrl({ file: path })}
              className={cn(
                "block w-full truncate text-left text-foreground/90",
                selectedPath === path && "bg-[rgba(196,92,38,0.10)] text-[var(--copper)]",
              )}
            >
              {path}
            </button>
          ))}
          {tree.length === 0 && (
            <div className="text-muted-foreground">
              Upload KiCad zip — hosted revision tree appears after ingest.
            </div>
          )}
        </div>
        <div className="min-w-0 min-h-[340px] bg-background" data-testid="files-viewer-pane">
          <Tabs defaultValue="viewer">
            <TabsList className="m-2">
              <TabsTrigger value="viewer">Viewer</TabsTrigger>
              <TabsTrigger value="art">Artifacts</TabsTrigger>
            </TabsList>
            <TabsContent value="viewer" className="px-4 pb-4">
              {viewerMissing ? (
                <div
                  className="flex h-[340px] flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 px-6 text-center text-sm text-muted-foreground"
                  data-testid="kicanvas-file-missing"
                  data-kicad-path={missingMarkerPath ?? undefined}
                >
                  That file is not in this revision.
                </div>
              ) : orgSlug && projectSlug && active && viewerPath && isKicadViewerPath(viewerPath) ? (
                <KicadFileView
                  key={`${active}:${viewerPath}`}
                  org={orgSlug}
                  proj={projectSlug}
                  revId={active}
                  gitOid={liveRev?.git_oid ?? null}
                  selectedPath={viewerPath}
                  tree={viewerTree}
                  catalogByteSizes={catalogByteSizes}
                  catalogReady={catalogReady && catalogRevId === active}
                  onFilesUrlChange={persistFilesUrl}
                />
              ) : (
                <div
                  className="flex h-[340px] flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 px-6 text-center text-sm text-muted-foreground"
                  data-testid="kicanvas-select-file"
                  data-kicad-path={selectedPath ?? undefined}
                >
                  {selectedPath && !active
                    ? "Loading this revision."
                    : selectedPath
                      ? "KiCanvas opens .kicad_sch, .kicad_pcb, and .kicad_pro files."
                      : "Select a .kicad_sch, .kicad_pcb, or .kicad_pro from the tree."}
                </div>
              )}
            </TabsContent>
            <TabsContent value="art" className="space-y-2 px-4 pb-4 text-sm">
              {live ? (
                artifacts.slice(0, 40).map((a) => (
                  <div key={a.id} className="rounded-md border border-border p-2 font-mono text-xs">
                    <div>
                      {a.kind}
                      {artifactMeta(a.metadata_json).kind_detail
                        ? `/${artifactMeta(a.metadata_json).kind_detail}`
                        : ""}{" "}
                      · {artifactMeta(a.metadata_json).label || a.path || a.id}
                    </div>
                    <div className="text-muted-foreground">
                      {shortOid(a.git_oid, 12)} · {a.byte_size ?? 0} B · {a.status}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">Turn on hw_collab_loop in Settings to list D1 artifacts.</p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
        </>
      )}
    </AppShell>
  );
}
